import { useContext, useState, useRef } from "react"
import { FormContext } from "../../../context/formContext"
import FormSubmitBtn from "../FormSubmitBtn"
import { Accordion, AccordionTab } from "primereact/accordion"
import { Button } from "primereact/button"
import { LogDebug } from "../../../helpers/logger"
import {
  isSkippableFieldType,
  isPasswordFieldName,
  isExcludedFieldName,
  conditionalMet,
  humanizeFieldName,
  stripHtml,
  formatValueForField,
  isJsonString,
  smoothScrollToTop,
  isMicroserviceChecked,
} from "../helpers/FormSummaryHelpers"
import FilePreview from "./FilePreview"

import styles from "../../../styles/FormSummary.module.css"

export default function FormSummary({
  blocksFetched,
  globalValues,
  prevSessionSummary = false,
}) {
  const form = useContext(FormContext)
  const summaryContainerRef = useRef(null)
  const [activeIndex, setActiveIndex] = useState([]) // keep as array for multi-mode

  // Use passed props if available, otherwise fallback to context
  const blocksObj = blocksFetched ?? form.blocksFetched ?? {}
  const formGlobalValues = globalValues ?? form.globalValues ?? {}

  // ensure blocks is an array and sorted by index
  const blocksArray = Array.isArray(blocksObj)
    ? blocksObj
    : Object.values(blocksObj)
  const sortedBlocks = blocksArray
    .slice()
    .sort((a, b) => (Number(a?.index) || 0) - (Number(b?.index) || 0))

  // exclude submit_block from summary display
  const blocksToRender = sortedBlocks.filter(
    (b) => b?.block_name !== "submit_block",
  )
  const allIndices = blocksToRender.map((_, i) => i)

  // Determines if a field should be rendered in the summary.
  function shouldRenderField(field, blockValues, rowValues = null) {
    if (!field || isExcludedFieldName(field.field_name)) return false

    const isVisible = field.field_visibility !== false
    const rawValue = rowValues
      ? rowValues?.[field.field_name]
      : blockValues?.[field.field_name]
    const hasValue = formatValueForField(rawValue, field, blockValues) !== "—"

    // Special exclusion: if field_visibility is false, but it has a value and is not a skippable type, show it.
    if (!isVisible && hasValue && !isSkippableFieldType(field.field_type)) {
      LogDebug(
        "FormSummary: Rendering field with field_visibility=false because it has a value and is not type sys/hidden:",
        {
          block_name: blockValues?.block_name,
          field_name: field.field_name,
          field_type: field.field_type,
          value: rawValue,
        },
      )
      return true // Show it anyway
    }

    return (
      isVisible &&
      !isSkippableFieldType(field.field_type) &&
      !isPasswordFieldName(field.field_name) &&
      conditionalMet(field, blockValues, rowValues)
    )
  }

  function handleAccordionTabChange(e) {
    // e.index can be a number or an array (when multiple). We want single-open behavior for user clicks:
    // - if user opens a tab, keep only that tab open
    // - if user closes the last open tab, collapse all
    const idx = e?.index
    if (Array.isArray(idx)) {
      if (idx.length === 0) {
        setActiveIndex([])
      } else {
        // pick the most recently toggled index (last in array)
        setActiveIndex([idx[idx.length - 1]])
      }
    } else {
      setActiveIndex([idx])
    }
  }

  // Scrolls the summary container or its scrollable parent to the top.
  function handleScrollToTop() {
    const container = summaryContainerRef.current
    if (!container) return

    const scrollableParent =
      container.closest(".p-dialog-content") || container.closest(".body-main")
    smoothScrollToTop(scrollableParent)
  }

  // helper: render the assessments JSON into a readable list
  function renderAssessments(raw) {
    if (raw == null || (typeof raw === "string" && raw.trim() === "")) {
      return "—"
    }

    let arr = null
    if (typeof raw === "string") {
      try {
        arr = JSON.parse(raw)
      } catch (e) {
        // not valid JSON — show raw text but avoid a single long JSON blob
        return <div className={styles.summaryFieldValueLine}>{raw}</div>
      }
    } else if (Array.isArray(raw)) {
      arr = raw
    } else {
      // unknown shape
      return "—"
    }

    if (!Array.isArray(arr) || arr.length === 0) return "—"

    return (
      <div className={styles.assessmentList}>
        {arr.map((a, i) => {
          const title = a?.assessment_name || a?.name || `Assessment ${i + 1}`
          const completed =
            a?.completed_assessment_id != null &&
            String(a.completed_assessment_id).trim() !== ""
          const required = Boolean(a?.required)

          return (
            <div
              key={i}
              className={styles.multiItemBox}
              style={{ marginBottom: "0.5rem", padding: "0.5rem" }}
            >
              <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
                {title}
              </div>

              <div className={styles.summaryFieldValueLine}>
                <span style={{ fontWeight: 600 }}>Status:</span>
                {completed ? (
                  <span
                    className="pi pi-check"
                    style={{
                      color: "#28a745",
                      marginLeft: "0.5rem",
                      marginRight: "0.5rem",
                    }}
                  />
                ) : (
                  <span
                    className="pi pi-times"
                    style={{
                      color: "#dc3545",
                      marginLeft: "0.5rem",
                      marginRight: "0.5rem",
                    }}
                  />
                )}
                {completed ? "Completed" : "Not completed"}
              </div>

              <div className={styles.summaryFieldValueLine}>
                <span style={{ fontWeight: 600 }}>Required:</span>{" "}
                {required ? "Yes" : "No"}
              </div>

              {a?.score != null && (
                <div className={styles.summaryFieldValueLine}>
                  <span style={{ fontWeight: 600 }}>Score:</span> {a.score}
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  // helper: render the microservices JSON into a readable list
  function renderMicroservices(blockValues) {
    // collect all microservice_* keys from the block values and determine checked state.
    const svcKeys = Object.keys(blockValues || {})
      .filter((k) => k.startsWith("microservice_"))
      .sort()
    const services = svcKeys.map((k) => {
      const v = blockValues[k]
      const checked = isMicroserviceChecked(v)
      const namePart = k.replace(/^microservice_/, "")
      return {
        key: k,
        label: humanizeFieldName(namePart),
        checked,
      }
    })

    return services.length === 0
      ? "—"
      : services.map((s) => (
          <div key={s.key} className={styles.summaryFieldValueLine}>
            {s.checked ? (
              <span
                className="pi pi-check"
                style={{ color: "#28a745", marginRight: "0.5rem" }}
              />
            ) : (
              <span
                className="pi pi-times"
                style={{ color: "#dc3545", marginRight: "0.5rem" }}
              />
            )}
            {s.label}
          </div>
        ))
  }

  function renderFeatures(blockValues) {
    // collect all features_* keys from the block values and determine checked state.
    const featKeys = Object.keys(blockValues || {})
      .filter((k) => k.startsWith("features_"))
      .sort()
    const features = featKeys.map((k) => {
      const v = blockValues[k]
      const checked = isMicroserviceChecked(v)
      const namePart = k.replace(/^features_/, "")
      return {
        key: k,
        label: humanizeFieldName(namePart),
        checked,
      }
    })

    return features.length === 0
      ? "—"
      : features.map((s) => (
          <div key={s.key} className={styles.summaryFieldValueLine}>
            {s.checked ? (
              <span
                className="pi pi-check"
                style={{ color: "#28a745", marginRight: "0.5rem" }}
              />
            ) : (
              <span
                className="pi pi-times"
                style={{ color: "#dc3545", marginRight: "0.5rem" }}
              />
            )}
            {s.label}
          </div>
        ))
  }

  function renderFormattedValue(formatted) {
    return isJsonString(formatted) ? (
      <pre className={styles.preWrap}>{formatted}</pre>
    ) : typeof formatted === "string" && formatted.includes("\n") ? (
      formatted.split("\n").map((l, i) => <div key={i}>{l}</div>)
    ) : (
      formatted
    )
  }

  // helper: render a rich text field (strip HTML)
  function renderRichText(field, rawValue) {
    const text = stripHtml(rawValue)
    return (
      <div key={field.field_name} className={styles.summaryFieldRow}>
        <div className={styles.summaryFieldLabel}>
          {field.field_label || humanizeFieldName(field.field_name)}
        </div>
        <div className={styles.summaryFieldValueBox}>{text || "—"}</div>
      </div>
    )
  }

  // helper: render a file preview
  function renderFilePreview(field, rawValue) {
    return (
      <div key={field.field_name} className={styles.summaryFieldRow}>
        <div className={styles.summaryFieldLabel}>
          {field.field_label || humanizeFieldName(field.field_name)}
        </div>
        <div className={styles.summaryFieldValueBox}>
          <FilePreview path={rawValue} fieldType={field.field_type} />
        </div>
      </div>
    )
  }

  // helper: render referral entity name
  function renderReferralEntity(field, rawValue) {
    const referralName = formGlobalValues?.referral?.referral_entity_name || "—"
    return (
      <div key={field.field_name} className={styles.summaryFieldRow}>
        <div className={styles.summaryFieldLabel}>
          {field.field_label || humanizeFieldName(field.field_name)}
        </div>
        <div className={styles.summaryFieldValueBox}>{referralName}</div>
      </div>
    )
  }

  // render multi_field rows: boxes with subfields (two-column rows)
  function renderMultiFieldBoxes(fieldDef, valuesArray, blockValues = {}) {
    const multiDefs = fieldDef.multi_fields || []

    if (!Array.isArray(valuesArray) || valuesArray.length === 0) {
      return <div className={styles.emptyMulti}>No items</div>
    }

    return (
      <div className={styles.multiList}>
        {valuesArray.map((row, idx) => (
          <div key={idx} className={styles.multiItemBox}>
            {/* render subfields in a two-column layout per your request */}
            <div className={styles.multiItemGrid}>
              {multiDefs
                .filter((mf) => shouldRenderField(mf, blockValues, row))
                .map((mf) => {
                  // fallback order: explicit label -> first option label -> humanized field name
                  const label =
                    mf.field_label ||
                    (Array.isArray(mf.field_options) &&
                      mf.field_options[0]?.label) ||
                    humanizeFieldName(mf.field_name)

                  const raw = row?.[mf.field_name]
                  // compute type once, reuse
                  const mfType = String(mf.field_type || "").toLowerCase()

                  // If this subfield is itself a multi_field, render a nested multi block recursively
                  if (mfType === "multi_field_nested") {
                    return (
                      <div
                        key={mf.field_name}
                        className={styles.multiItemGridRow}
                      >
                        <div className={styles.multiItemGridLabel}>{label}</div>
                        <div className={styles.multiItemGridValue}>
                          {renderMultiFieldBoxes(mf, raw, row)}
                        </div>
                      </div>
                    )
                  }

                  const formatted = formatValueForField(raw, mf)
                  return (
                    <div
                      key={mf.field_name}
                      className={styles.multiItemGridRow}
                    >
                      <div className={styles.multiItemGridLabel}>{label}</div>
                      <div
                        className={styles.multiItemGridValue}
                        style={
                          isJsonString(formatted)
                            ? {
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                              }
                            : {}
                        }
                      >
                        {renderFormattedValue(formatted)}
                      </div>
                    </div>
                  )
                })}
            </div>

            {/* removed automatic display of row.date_created to keep summary clean */}
          </div>
        ))}
      </div>
    )
  }

  // render a single top-level field row (label + value box)
  function renderFieldRow(field, rawValue, blockValues = {}) {
    // skip fields with internal types or password fields
    if (!shouldRenderField(field, blockValues, null)) return null

    let label = field.field_label || humanizeFieldName(field.field_name)
    const fieldType = String(field.field_type || "").toLowerCase()

    // file_image: render a preview
    if (fieldType === "file_image" || fieldType === "file") {
      return rawValue ? renderFilePreview(field, rawValue) : null
    }

    // textarea_rich: strip HTML and show plain text
    if (fieldType === "textarea_rich") {
      return renderRichText(field, rawValue)
    }

    // special rendering for the enterprise_microservices | organization_microservices canonical field:
    // - do NOT render a label (accordion title covers it)
    // - show a small check icon before each selected service and an X icon before each unchecked
    if (
      String(field.field_name) === "organization_microservices" ||
      String(field.field_name) === "enterprise_microservices"
    ) {
      return (
        <div key={field.field_name} className={styles.summaryFieldBlock}>
          {/* full-width value area (no label) */}
          <div className={styles.summaryFieldValue} style={{ width: "100%" }}>
            {renderMicroservices(blockValues)}
          </div>
        </div>
      )
    }

    // special rendering for the enterprise_features canonical field
    if (String(field.field_name) === "enterprise_features") {
      return (
        <div key={field.field_name} className={styles.summaryFieldBlock}>
          <div className={styles.summaryFieldValue} style={{ width: "100%" }}>
            {renderFeatures(blockValues)}
          </div>
        </div>
      )
    }

    // special rendering for assessments JSON (session__assessment -> assessments)
    if (String(field.field_name) === "assessments") {
      const node = renderAssessments(rawValue)
      return (
        <div key={field.field_name} className={styles.summaryFieldBlock}>
          <div>{node}</div>
        </div>
      )
    }

    // If referral_entity_id is not null and > 0, show referral_entity_name from formGlobalValues
    if (
      String(field.field_name) === "referral_entity_id" &&
      Number(rawValue) > 0
    ) {
      return renderReferralEntity(field, rawValue)
    }

    const formatted = formatValueForField(rawValue, field, blockValues)
    const valueNodes = renderFormattedValue(formatted)

    return (
      <div key={field.field_name} className={styles.summaryFieldRow}>
        <div className={styles.summaryFieldLabel}>{label}</div>
        <div className={styles.summaryFieldValueBox}>{valueNodes}</div>
      </div>
    )
  }

  // returns true if any subfield in the multi_field has a meaningful value
  function hasMeaningfulMultiFieldValues(
    fieldDef,
    valuesArray,
    blockValues = {},
  ) {
    const defs = fieldDef?.multi_fields || []
    if (!Array.isArray(valuesArray) || valuesArray.length === 0) return false

    for (const row of valuesArray) {
      // check each subfield definition that would be rendered
      for (const mf of defs) {
        if (!shouldRenderField(mf, blockValues, row)) continue

        // If nested multi_field, recurse to see if nested rows contain meaningful data
        if (String(mf.field_type || "").toLowerCase() === "multi_field") {
          const nestedVals = row?.[mf.field_name]
          if (hasMeaningfulMultiFieldValues(mf, nestedVals, row)) return true
          continue
        }

        const raw = row?.[mf.field_name]
        const formatted = formatValueForField(raw, mf)
        if (formatted !== "—") return true
      }
    }

    return false
  }

  return (
    <>
      <div className={styles.formSummaryContainer} ref={summaryContainerRef}>
        <div className={styles.formSummaryHeader}>
          <div>Form Summary</div>
          <div className={styles.formSummaryHeaderBtns}>
            <Button
              label="Expand All"
              icon="pi pi-angle-double-down"
              iconPos="left"
              className={styles.formSummaryHeaderBtn}
              onClick={() => setActiveIndex(allIndices)}
            />
            <Button
              label="Collapse All"
              icon="pi pi-angle-double-up"
              iconPos="left"
              className={styles.formSummaryHeaderBtn}
              onClick={() => setActiveIndex([])}
            />
          </div>
        </div>

        <div className={styles.formSummaryBlocks}>
          <Accordion
            multiple
            activeIndex={activeIndex}
            onTabChange={handleAccordionTabChange}
          >
            {blocksToRender.map((block) => {
              const blockValues = formGlobalValues?.[block.block_name] ?? {}

              return (
                <AccordionTab
                  key={block.block_name}
                  header={block.block_title || block.block_name}
                >
                  <div className={styles.summaryBlockWrapper}>
                    <div className={styles.summaryBlockBody}>
                      {(block?.fields ?? [])
                        .filter((f) => shouldRenderField(f, blockValues, null))
                        .map((field) => {
                          const name = field.field_name
                          const rawValue = blockValues?.[name]

                          if (field.field_type === "multi_field") {
                            // if the multi_field has no meaningful subfield values (only system date_created),
                            // render the main field with the empty indicator instead of showing empty subfield boxes.
                            const hasValues = hasMeaningfulMultiFieldValues(
                              field,
                              rawValue,
                              blockValues,
                            )
                            if (!hasValues) {
                              return (
                                <div
                                  key={name}
                                  className={styles.summaryFieldRow}
                                >
                                  <div className={styles.summaryFieldLabel}>
                                    {field.field_label || name}
                                  </div>
                                  <div className={styles.summaryFieldValueBox}>
                                    —
                                  </div>
                                </div>
                              )
                            }
                            return (
                              <div
                                key={name}
                                className={styles.summaryFieldBlock}
                              >
                                <div className={styles.summaryFieldLabel}>
                                  {field.field_label || name}
                                </div>
                                <div className={styles.summaryFieldValue}>
                                  {renderMultiFieldBoxes(
                                    field,
                                    rawValue,
                                    blockValues,
                                  )}
                                </div>
                              </div>
                            )
                          }

                          // individual fields handled in renderFieldRow (which also skips skippable types)
                          return renderFieldRow(field, rawValue, blockValues)
                        })}
                    </div>
                  </div>
                </AccordionTab>
              )
            })}
          </Accordion>
        </div>

        <div className={styles.backToTopContainer}>
          <Button
            label="Back to Top"
            icon="pi pi-arrow-up"
            onClick={handleScrollToTop}
            className={styles.formSummaryHeaderBtn}
            size="small"
          />
        </div>
      </div>

      {!prevSessionSummary && (
        <div className={styles.submitBlockContainer}>
          <FormSubmitBtn />
        </div>
      )}
    </>
  )
}
